import Immutable, { fromJS } from 'immutable';

import { get } from 'lodash-es';

import { getErrorMessage } from '../utils';
import * as types from './actionTypes';
import * as dashboardTypes from '../Dashboard/actionTypes';
import { locale } from '../Tools/reducers';
import * as systemTypes from '../System/actionTypes';
import {
  addQuestionnaireMap,
  retrieveSalesRouteList,
  transformResult,
  getSalesRouteTransform,
  getProductDescriptionsTransform,
  getQuestionnairesTransform,
  buildQuestionnaireBlock,
  buildLoansBlock,
  buildQuotationBlock,
  buildExtraDataBlock,
  buildGeneralDataBlock,
  buildOffersBlock,
  buildSubscriptionBlock,
  buildDocumentsBlock,
  changePersonTypeContract,
} from './Transform';
import { findSalesRouteIndexById, findSalesRouteById } from './utils';
import { processErrors, getSuccessMessage, computeMessage } from '../../Utils/Utils';
import { advancementStatusEnum, statusSalesRouteEnum } from './enum';

let newState;
let newSalesRoutes;
let newSalesRoute;
let newQuestionnaires;
let newProducts;

/**
 * Set/Update Salesroute in store, return updated state and Salesroute
 */
const getSalesRouteAndState = ({ state, action }) => {
  let tmpState = state;

  try {
    newSalesRoute = getSalesRouteTransform({ salesRoute: get(action, 'data') });
    if (!state.getIn(['list']) || state.getIn(['list']).size === 0) {
      tmpState = state.setIn(['list'], fromJS([newSalesRoute]));
    } else {
      const newSalesRouteIndex = findSalesRouteIndexById({ list: state.getIn(['list']), id: get(action, 'id') });
      if (newSalesRouteIndex >= 0) tmpState = state.setIn(['list', newSalesRouteIndex], fromJS(newSalesRoute));
    }
  } catch (e) {
    console.error(`getSalesRouteAndState: ${e.stack}`);
  }

  return { state: tmpState, salesRouteJs: newSalesRoute };
};

/**
 * Build block and add it to state
 * @param {String} block Block name
 * @param {Immutable.Object} state Current state to get data from and to add new block
 * @param {Object} salesRoute SalesRoute to build block from
 * @param {Boolean} reset Reset block to empty data
 * @returns new state with block added
 */
const addBlockToState = ({ block, state, salesRoute, reset }) => {
  let tmpState;
  let blockData;
  const questionnaires = state.getIn(['questionnaires']) ? state.getIn(['questionnaires']).toJS() : [];
  const products = state.getIn(['products']) ? state.getIn(['products']).toJS() : [];
  const relations = state.getIn(['relations']) ? state.getIn(['relations']).toJS() : [];
  const strings = state.get('strings');

  try {
    if (reset) {
      tmpState = state.setIn([block], Immutable.fromJS({}));
    } else {
      switch (block) {
        case 'blockQuestionnaire':
          blockData = buildQuestionnaireBlock({ salesRoute, questionnaires, strings });
          break;
        case 'blockLoans':
          blockData = buildLoansBlock({ salesRoute, strings });
          break;
        case 'blockQuotation':
          blockData = buildQuotationBlock({ salesRoute, relations, strings });
          break;
        case 'blockExtraData':
          blockData = buildExtraDataBlock({ salesRoute, strings });
          break;
        case 'blockGeneralData':
          blockData = buildGeneralDataBlock({ salesRoute, strings });
          break;
        case 'blockOffers':
          blockData = buildOffersBlock({ salesRoute, questionnaires, products, strings });
          break;
        case 'blockSubscription':
          blockData = buildSubscriptionBlock({ salesRoute, strings });
          break;
        case 'blockDocuments':
          // TODO when print is working
          blockData = buildDocumentsBlock({ salesRoute, strings });
          break;
        default:
          break;
      }

      tmpState = state.setIn([block], Immutable.fromJS(blockData));
    }
  } catch (e) {
    console.error(`addBlockToState ${block}: ${e.stack}`);
  }

  return tmpState;
};

const partialUpdateSalesrouteContractSuccess = ({ state, action }) => {
  let { state: tmpState, salesRouteJs } = getSalesRouteAndState({ state, action });

  // build offers block
  tmpState = addBlockToState({ block: 'blockOffers', state: tmpState, salesRoute: salesRouteJs });

  // build subscription block
  tmpState = addBlockToState({ block: 'blockSubscription', state: tmpState, salesRoute: salesRouteJs });

  return tmpState;
};

const SalesRoutes = (state, action) => {
  try {
    switch (action.type) {
      // LOCALE
      case systemTypes.FETCH_USER_LANGUAGE: {
        return state.set('strings', locale(action?.data?.language, 'salesroutes'));
      }

      /**
       * PATCH SALESROUTE
       */
      case types.PATCH_SALESROUTE_REQUEST: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_PATCH_SALESROUTE_REQUEST);
        return newState;
      }

      case types.PATCH_SALESROUTE_SUCCESS: {
        let { state: newState, salesRouteJs } = getSalesRouteAndState({ state, action });

        // build questionnaire block
        newState = addBlockToState({ block: 'blockQuestionnaire', state: newState, salesRoute: salesRouteJs });

        // build loans block
        newState = addBlockToState({ block: 'blockLoans', state: newState, salesRoute: salesRouteJs });

        // build quotation block
        newState = addBlockToState({
          block: 'blockQuotation',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            !get(salesRouteJs, 'parties', []).length &&
            salesRouteJs.status !== statusSalesRouteEnum.PRESIMULATED &&
            salesRouteJs.status !== statusSalesRouteEnum.LOANSDESCRIBED,
        });

        // build extra data block
        newState = addBlockToState({ block: 'blockExtraData', state: newState, salesRoute: salesRouteJs });

        // build offers block
        newState = addBlockToState({
          block: 'blockOffers',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            salesRouteJs.status === statusSalesRouteEnum.COMPUTED ||
            salesRouteJs.status === statusSalesRouteEnum.CREATED ||
            salesRouteJs.status === statusSalesRouteEnum.PRESIMULATED ||
            salesRouteJs.status === statusSalesRouteEnum.LOANSDESCRIBED ||
            salesRouteJs.status === statusSalesRouteEnum.COVEREDDESCRIBED,
        });

        // build subscription block
        newState = addBlockToState({
          block: 'blockSubscription',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            (salesRouteJs.status !== statusSalesRouteEnum.SIMULATED &&
              salesRouteJs.status !== statusSalesRouteEnum.SUBSCRIBED) ||
            !salesRouteJs.contracts,
        });

        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_PATCH_SALESROUTE_SUCCESS);
        return newState;
      }

      case types.PATCH_SALESROUTE_FAIL: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_PATCH_SALESROUTE_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      /**
       * RESET FORM SALESROUTE
       */
      case types.RESET_FORM_SALESROUTE_REQUEST: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_RESET_FORM_SALESROUTE_REQUEST);
        return newState;
      }
      case types.RESET_FORM_SALESROUTE_SUCCESS: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_RESET_FORM_SALESROUTE_SUCCESS);
        return newState;
      }

      /**
       * PARTIAL UPDATE SALESROUTE CONTRACT
       */
      case types.PARTIAL_UPDATE_SALESROUTE_CONTRACT_REQUEST: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_SUBSCRIPTION_BLOCK_REQUEST);
        newState = newState.setIn(['loading', 'isLoadingSubscriptionBlock'], true);
        newState = newState.setIn(['blockDocuments'], Immutable.fromJS({}));
        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_CONTRACT_SUCCESS: {
        newState = partialUpdateSalesrouteContractSuccess({ state, action });
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_SUBSCRIPTION_BLOCK_SUCCESS);
        newState = newState.setIn(['loading', 'isLoadingSubscriptionBlock'], false);
        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_CONTRACT_FAIL: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_SUBSCRIPTION_BLOCK_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        newState = newState.setIn(['loading', 'isLoadingSubscriptionBlock'], false);
        return newState;
      }

      /**
       * PARTIAL UPDATE SALESROUTE CONTRACT PRINT
       */
      case types.PARTIAL_UPDATE_SALESROUTE_CONTRACT_PRINT_REQUEST: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_PRINT_SALESROUTE_REQUEST);
        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_CONTRACT_PRINT_SUCCESS: {
        newState = partialUpdateSalesrouteContractSuccess({ state, action });
        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_CONTRACT_PRINT_FAIL: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_PRINT_SALESROUTE_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      /**
       * PARTIAL UPDATE SALESROUTE SUBSCRIPTION
       */
      case types.PARTIAL_UPDATE_SALESROUTE_SUBSCRIPTION_REQUEST: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_FINALIZE_SALESROUTE_REQUEST);
        newState = newState.setIn(['blockDocuments'], Immutable.fromJS({}));
        newState = newState.setIn(['loading', 'isLoadingDocumentsBlock'], true);
        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_SUBSCRIPTION_SUCCESS: {
        let { state: newState, salesRouteJs } = getSalesRouteAndState({ state, action });

        // build quotation block
        newState = addBlockToState({
          block: 'blockQuotation',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            !get(salesRouteJs, 'parties', []).length &&
            salesRouteJs.status !== statusSalesRouteEnum.PRESIMULATED &&
            salesRouteJs.status !== statusSalesRouteEnum.LOANSDESCRIBED,
        });

        // build subscription block
        newState = addBlockToState({ block: 'blockSubscription', state: newState, salesRoute: salesRouteJs });

        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_SUBSCRIPTION_FAIL: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_FINALIZE_SALESROUTE_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        newState = newState.setIn(['loading', 'isLoadingDocumentsBlock'], false);
        return newState;
      }

      /**
       * PARTIAL_UPDATE_SALESROUTE_QUOTATIONS
       */
      case types.PARTIAL_UPDATE_SALESROUTE_QUOTATIONS_REQUEST: {
        newState = state.setIn(['loading', 'isLoadingOffersBlock'], true);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_OFFERS_BLOCK_REQUEST);
        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_QUOTATIONS_SUCCESS: {
        // set/update salesroute in store
        let { state: newState, salesRouteJs } = getSalesRouteAndState({ state, action });

        // build quotation block
        newState = addBlockToState({ block: 'blockQuotation', state: newState, salesRoute: salesRouteJs });

        // build extra data block
        newState = addBlockToState({ block: 'blockExtraData', state: newState, salesRoute: salesRouteJs });

        // build general data block
        newState = addBlockToState({ block: 'blockGeneralData', state: newState, salesRoute: salesRouteJs });

        // build subscription block
        newState = addBlockToState({
          block: 'blockSubscription',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            (salesRouteJs.status !== statusSalesRouteEnum.SIMULATED &&
              salesRouteJs.status !== statusSalesRouteEnum.SUBSCRIBED) ||
            !salesRouteJs.contracts,
        });

        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_QUOTATIONS_FAIL: {
        newState = state.setIn(['loading', 'isLoadingOffersBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_OFFERS_BLOCK_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      // SIMULATE_QUOTATIONS
      case types.SIMULATE_QUOTATIONS_SUCCESS: {
        // set/update salesroute in store
        let { state: newState, salesRouteJs } = getSalesRouteAndState({ state, action });

        // build quotation block
        newState = addBlockToState({ block: 'blockQuotation', state: newState, salesRoute: salesRouteJs });

        // build extra data block
        newState = addBlockToState({ block: 'blockExtraData', state: newState, salesRoute: salesRouteJs });

        // build offers block
        newState = addBlockToState({ block: 'blockOffers', state: newState, salesRoute: salesRouteJs });

        // build subscription block
        newState = addBlockToState({
          block: 'blockSubscription',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            (salesRouteJs.status !== statusSalesRouteEnum.SIMULATED &&
              salesRouteJs.status !== statusSalesRouteEnum.SUBSCRIBED) ||
            !salesRouteJs.contracts,
        });

        newState = newState.setIn(['loading', 'isLoadingOffersBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_OFFERS_BLOCK_SUCCESS);
        return newState;
      }

      case types.SIMULATE_QUOTATIONS_FAIL: {
        newState = state.setIn(['blockOffers'], Immutable.fromJS({}));
        newState = newState.setIn(['loading', 'isLoadingOffersBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_OFFERS_BLOCK_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      // SIMULATE_QUOTATIONS
      case types.SUBSCRIBE_CONTRACTS_SUCCESS: {
        // set/update salesroute in store
        let { state: newState, salesRouteJs } = getSalesRouteAndState({ state, action });

        // build subscription block
        newState = addBlockToState({
          block: 'blockSubscription',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            (salesRouteJs.status !== statusSalesRouteEnum.SIMULATED &&
              salesRouteJs.status !== statusSalesRouteEnum.SUBSCRIBED) ||
            !salesRouteJs.contracts,
        });

        // build documents block
        newState = addBlockToState({ block: 'blockDocuments', state: newState, salesRoute: salesRouteJs });

        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_FINALIZE_SALESROUTE_SUCCESS);
        newState = newState.setIn(['loading', 'isLoadingDocumentsBlock'], false);
        return newState;
      }

      case types.SUBSCRIBE_CONTRACTS_FAIL: {
        newState = state.setIn(['blockDocuments'], Immutable.fromJS({}));
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_FINALIZE_SALESROUTE_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        newState = newState.setIn(['loading', 'isLoadingDocumentsBlock'], false);
        return newState;
      }

      /**
       * FETCH_QUESTIONNAIRE
       */
      case types.FETCH_QUESTIONNAIRE_REQUEST: {
        newState = state.setIn(['loading', 'isLoadingQuestionnaire'], true);
        return newState;
      }

      case types.FETCH_QUESTIONNAIRE_SUCCESS: {
        newQuestionnaires = newState.getIn(['questionnaireMap']) ? newState.getIn(['questionnaireMap']).toJS() : [];
        newQuestionnaires = Immutable.fromJS(
          addQuestionnaireMap({ questionnaires1: newQuestionnaires, questionnaires2: get(action, 'data') })
        );
        newState = state.setIn(['questionnaireMap'], newQuestionnaires);
        newState = newState.setIn(['loading', 'isLoadingQuestionnaire'], false);
        return newState;
      }

      case types.FETCH_QUESTIONNAIRE_FAIL: {
        newState = state.setIn(['loading', 'isLoadingQuestionnaire'], false);
        newState = newState.setIn(
          ['alerts', 'errors'],
          fromJS([{ t: Date.now(), msg: get(action, 'error[0]', get(action.error, 'error')) }])
        );
        return newState;
      }

      /**
       * FETCH_SALESROUTES
       */
      case types.FETCH_SALESROUTES_REQUEST: {
        newState = state.setIn(['loading', 'isLoadingSalesRoutes'], true);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_FETCH_SALESROUTES_REQUEST);
        newState = newState.setIn(['current'], null);
        return newState;
      }

      case types.FETCH_SALESROUTES_SUCCESS: {
        newSalesRoutes = Immutable.fromJS(
          retrieveSalesRouteList({
            salesRoutes: get(action, 'data', []),
            questionnaires: state.getIn(['questionnaires']).toJS(),
          })
        );

        // mergeDeep caused a bug to the list, so I disabled it. (1 line was "gone" or 2 line got the same informations)
        // Why was it there at first? We need the last versions of the list.
        // newSalesRoutes = state.getIn(['list']) ? newSalesRoutes.mergeDeep(state.getIn(['list'])) : newSalesRoutes;

        newState = state.setIn(['list'], newSalesRoutes);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_FETCH_SALESROUTES_SUCCESS);
        newState = newState.setIn(['loading', 'isLoadingSalesRoutes'], false);
        return newState;
      }

      case types.FETCH_SALESROUTES_FAIL: {
        newState = state.setIn(['loading', 'isLoadingSalesRoutes'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_FETCH_SALESROUTES_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      /**
       * EXTEND_SALESROUTES_SUCCESS
       */
      case types.EXTEND_SALESROUTES_REQUEST: {
        newState = state.setIn(['loading', 'isExtendingSalesRoutes'], true);
        return newState;
      }

      case types.EXTEND_SALESROUTES_SUCCESS: {
        newQuestionnaires = Immutable.fromJS(getQuestionnairesTransform({ questionnaires: action.data }));
        newState = state.setIn(['questionnaires'], newQuestionnaires);
        newSalesRoutes = Immutable.fromJS(
          retrieveSalesRouteList({
            salesRoutes: state.getIn(['list']).toJS(),
            questionnaires: getQuestionnairesTransform({ questionnaires: action.data }),
          })
        );
        newState = newState.setIn(['list'], newSalesRoutes);
        newState = newState.setIn(['loading', 'isExtendingSalesRoutes'], false);
        return newState;
      }

      case types.EXTEND_SALESROUTES_FAIL: {
        newState = state.setIn(['loading', 'isExtendingSalesRoutes'], false);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      /**
       * PARTIAL_UPDATE_SALESROUTE_ANSWERS
       */
      case types.PARTIAL_UPDATE_SALESROUTE_ANSWERS_REQUEST: {
        newState = state.setIn(['loading', 'isLoadingLoansBlock'], true);
        newState = newState.setIn(['loading', 'isLoadingQuotationBlock'], true);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_DESCRIBE_SALESROUTE_REQUEST);
        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_ANSWERS_SUCCESS: {
        // set/update salesroute in store
        let { state: newState, salesRouteJs } = getSalesRouteAndState({ state, action });

        // build questionnaire block
        newState = addBlockToState({ block: 'blockQuestionnaire', state: newState, salesRoute: salesRouteJs });

        // build quotation block
        newState = addBlockToState({
          block: 'blockQuotation',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            !get(salesRouteJs, 'parties', []).length &&
            salesRouteJs.status !== statusSalesRouteEnum.PRESIMULATED &&
            salesRouteJs.status !== statusSalesRouteEnum.LOANSDESCRIBED,
        });

        // build general data block
        newState = addBlockToState({
          block: 'blockGeneralData',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            salesRouteJs.status === statusSalesRouteEnum.CREATED ||
            salesRouteJs.status === statusSalesRouteEnum.COMPUTED ||
            salesRouteJs.status === statusSalesRouteEnum.PRESIMULATED,
        });

        // build offers block
        newState = addBlockToState({
          block: 'blockOffers',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            salesRouteJs.status === statusSalesRouteEnum.CREATED ||
            salesRouteJs.status === statusSalesRouteEnum.COMPUTED ||
            salesRouteJs.status === statusSalesRouteEnum.PRESIMULATED ||
            salesRouteJs.status === statusSalesRouteEnum.LOANSDESCRIBED ||
            salesRouteJs.status === statusSalesRouteEnum.COVEREDDESCRIBED,
        });

        // build subscription block
        newState = addBlockToState({
          block: 'blockSubscription',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            (salesRouteJs.status !== statusSalesRouteEnum.SIMULATED &&
              salesRouteJs.status !== statusSalesRouteEnum.SUBSCRIBED) ||
            !salesRouteJs.contracts,
        });

        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_ANSWERS_FAIL: {
        newState = state.setIn(['loading', 'isLoadingLoansBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingQuotationBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_DESCRIBE_SALESROUTE_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      /**
       * PARTIAL_UPDATE_SALESROUTE_LOANS
       */
      case types.PARTIAL_UPDATE_SALESROUTE_LOANS_REQUEST: {
        newState = state.setIn(['loading', 'isLoadingQuotationBlock'], true);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_LOANS_BLOCK_REQUEST);
        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_LOANS_SUCCESS: {
        // set/update salesroute in store
        let { state: newState, salesRouteJs } = getSalesRouteAndState({ state, action });

        // build loans block
        newState = addBlockToState({ block: 'blockLoans', state: newState, salesRoute: salesRouteJs });

        // build quotation block
        newState = addBlockToState({
          block: 'blockQuotation',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            !get(salesRouteJs, 'parties', []).length &&
            salesRouteJs.status !== statusSalesRouteEnum.PRESIMULATED &&
            salesRouteJs.status !== statusSalesRouteEnum.LOANSDESCRIBED,
        });

        // build offers block
        newState = addBlockToState({
          block: 'blockOffers',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            salesRouteJs.status === statusSalesRouteEnum.COMPUTED ||
            salesRouteJs.status === statusSalesRouteEnum.CREATED ||
            salesRouteJs.status === statusSalesRouteEnum.PRESIMULATED ||
            salesRouteJs.status === statusSalesRouteEnum.LOANSDESCRIBED ||
            salesRouteJs.status === statusSalesRouteEnum.COVEREDDESCRIBED,
        });

        newState = newState.setIn(['loading', 'isLoadingQuotationBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_LOANS_BLOCK_SUCCESS);

        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_LOANS_FAIL: {
        newState = state.setIn(['loading', 'isLoadingQuotationBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_LOANS_BLOCK_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      /**
       * PARTIAL_UPDATE_SALESROUTE_EXTRADATA
       */
      case types.PARTIAL_UPDATE_SALESROUTE_EXTRADATA_REQUEST: {
        newState = state.setIn(['loading', 'isLoadingExtraDataBlock'], true);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_EXTRADATA_BLOCK_REQUEST);
        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_EXTRADATA_SUCCESS: {
        // set/update salesroute in store
        let { state: newState, salesRouteJs } = getSalesRouteAndState({ state, action });

        // build quotation block
        newState = addBlockToState({
          block: 'blockQuotation',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            !get(salesRouteJs, 'parties', []).length &&
            salesRouteJs.status !== statusSalesRouteEnum.PRESIMULATED &&
            salesRouteJs.status !== statusSalesRouteEnum.LOANSDESCRIBED,
        });

        // build extra data block
        newState = addBlockToState({ block: 'blockExtraData', state: newState, salesRoute: salesRouteJs });

        // build general data block
        newState = addBlockToState({ block: 'blockGeneralData', state: newState, salesRoute: salesRouteJs });

        // build offers block
        newState = addBlockToState({
          block: 'blockOffers',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            salesRouteJs.status === statusSalesRouteEnum.COMPUTED ||
            salesRouteJs.status === statusSalesRouteEnum.CREATED ||
            salesRouteJs.status === statusSalesRouteEnum.PRESIMULATED ||
            salesRouteJs.status === statusSalesRouteEnum.LOANSDESCRIBED ||
            salesRouteJs.status === statusSalesRouteEnum.COVEREDDESCRIBED,
        });

        // build subscription block
        newState = addBlockToState({
          block: 'blockSubscription',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            (salesRouteJs.status !== statusSalesRouteEnum.SIMULATED &&
              salesRouteJs.status !== statusSalesRouteEnum.SUBSCRIBED) ||
            !salesRouteJs.contracts,
        });

        newState = newState.setIn(['loading', 'isLoadingExtraDataBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_EXTRADATA_BLOCK_SUCCESS);

        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_EXTRADATA_FAIL: {
        newState = state.setIn(['loading', 'isLoadingExtraDataBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_EXTRADATA_BLOCK_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      /**
       * PARTIAL_UPDATE_SALESROUTE_GENERALDATA
       */
      case types.PARTIAL_UPDATE_SALESROUTE_GENERALDATA_REQUEST: {
        newState = state.setIn(['loading', 'isLoadingGeneralDataBlock'], true);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_GENERALDATA_BLOCK_REQUEST);
        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_GENERALDATA_SUCCESS: {
        // set/update salesroute in store
        let { state: newState, salesRouteJs } = getSalesRouteAndState({ state, action });

        // build quotation block
        newState = addBlockToState({
          block: 'blockQuotation',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            !get(salesRouteJs, 'parties', []).length &&
            salesRouteJs.status !== statusSalesRouteEnum.PRESIMULATED &&
            salesRouteJs.status !== statusSalesRouteEnum.LOANSDESCRIBED,
        });

        // build extra data block
        newState = addBlockToState({ block: 'blockExtraData', state: newState, salesRoute: salesRouteJs });

        // build general data block
        newState = addBlockToState({ block: 'blockGeneralData', state: newState, salesRoute: salesRouteJs });

        // build offers block
        newState = addBlockToState({
          block: 'blockOffers',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            salesRouteJs.status === statusSalesRouteEnum.COMPUTED ||
            salesRouteJs.status === statusSalesRouteEnum.CREATED ||
            salesRouteJs.status === statusSalesRouteEnum.PRESIMULATED ||
            salesRouteJs.status === statusSalesRouteEnum.LOANSDESCRIBED ||
            salesRouteJs.status === statusSalesRouteEnum.COVEREDDESCRIBED,
        });

        // build subscription block
        newState = addBlockToState({
          block: 'blockSubscription',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            (salesRouteJs.status !== statusSalesRouteEnum.SIMULATED &&
              salesRouteJs.status !== statusSalesRouteEnum.SUBSCRIBED) ||
            !salesRouteJs.contracts,
        });

        newState = newState.setIn(['loading', 'isLoadingGeneralDataBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_GENERALDATA_BLOCK_SUCCESS);

        return newState;
      }

      case types.PARTIAL_UPDATE_SALESROUTE_GENERALDATA_FAIL: {
        newState = state.setIn(['loading', 'isLoadingGeneralDataBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_GENERALDATA_BLOCK_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      // COMPUTE_QUESTIONNAIRES
      case types.COMPUTE_QUESTIONNAIRES_SUCCESS: {
        // set/update salesroute in store
        let { state: newState } = getSalesRouteAndState({ state, action });

        newState = newState.setIn(['blockOffers'], Immutable.fromJS({}));

        return newState;
      }

      case types.COMPUTE_QUESTIONNAIRES_FAIL: {
        newState = state.setIn(['loading', 'isLoadingLoansBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingQuotationBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_DESCRIBE_SALESROUTE_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        newState = newState.setIn(['blockOffers'], Immutable.fromJS({}));
        return newState;
      }

      // FETCH_QUOTATION_DESCRIPTIONS
      case types.FETCH_QUOTATION_DESCRIPTIONS_SUCCESS: {
        // set/update salesroute in store
        let { state: newState, salesRouteJs } = getSalesRouteAndState({ state, action });

        // build loans block
        newState = addBlockToState({
          block: 'blockLoans',
          state: newState,
          salesRoute: salesRouteJs,
          // reset: !(salesRouteJs.parties?.length && some(salesRouteJs.parties, p => p.addresses?.length)),
        });

        // build quotation block
        newState = addBlockToState({
          block: 'blockQuotation',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            !get(salesRouteJs, 'parties', []).length &&
            salesRouteJs.status !== statusSalesRouteEnum.PRESIMULATED &&
            salesRouteJs.status !== statusSalesRouteEnum.LOANSDESCRIBED,
        });

        newState = newState.setIn(['loading', 'isLoadingLoansBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingQuotationBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_DESCRIBE_SALESROUTE_SUCCESS);
        return newState;
      }

      case types.FETCH_QUOTATION_DESCRIPTIONS_FAIL: {
        newState = state.setIn(['loading', 'isLoadingLoansBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingQuotationBlock'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_DESCRIBE_SALESROUTE_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      /**
       * FETCH_SALESROUTE
       */
      case types.FETCH_SALESROUTE_REQUEST: {
        newState = state.setIn(['loading', 'isLoadingSalesRoute'], true);
        newState = newState.setIn(['loading', 'isLoadingQuestionnaireBlock'], true);
        newState = newState.setIn(['loading', 'isLoadingLoansBlock'], true);
        newState = newState.setIn(['loading', 'isLoadingQuotationBlock'], true);
        newState = newState.setIn(['loading', 'isLoadingOffersBlock'], true);
        newState = newState.setIn(['loading', 'isLoadingSubscriptionBlock'], true);
        newState = newState.setIn(['loading', 'isLoadingDocumentsBlock'], true);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_FETCH_SALESROUTE_REQUEST);
        return newState;
      }

      case types.FETCH_SALESROUTE_SUCCESS: {
        // set/update salesroute in store
        let { state: newState, salesRouteJs } = getSalesRouteAndState({ state, action });

        newState = newState.setIn(['loading', 'isLoadingSalesRoute'], false);

        // build loans block
        newState = addBlockToState({
          block: 'blockLoans',
          state: newState,
          salesRoute: salesRouteJs,
          // reset: !(salesRouteJs.parties?.length && some(salesRouteJs.parties, p => p.addresses?.length)),
        });

        // build extra data block
        newState = addBlockToState({
          block: 'blockExtraData',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            salesRouteJs.status === statusSalesRouteEnum.COMPUTED ||
            salesRouteJs.status === statusSalesRouteEnum.CREATED ||
            salesRouteJs.status === statusSalesRouteEnum.PRESIMULATED ||
            salesRouteJs.status === statusSalesRouteEnum.LOANSDESCRIBED,
        });

        // build general data block
        newState = addBlockToState({
          block: 'blockGeneralData',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            salesRouteJs.status === statusSalesRouteEnum.COMPUTED ||
            salesRouteJs.status === statusSalesRouteEnum.CREATED ||
            salesRouteJs.status === statusSalesRouteEnum.PRESIMULATED ||
            salesRouteJs.status === statusSalesRouteEnum.LOANSDESCRIBED,
        });

        // build documents block
        newState = addBlockToState({
          block: 'blockDocuments',
          state: newState,
          salesRoute: salesRouteJs,
          reset: salesRouteJs.status !== statusSalesRouteEnum.SUBSCRIBED,
        });

        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_FETCH_SALESROUTE_SUCCESS);

        return newState;
      }

      case types.FETCH_SALESROUTE_FAIL: {
        newState = state.setIn(['loading', 'isLoadingSalesRoute'], false);
        newState = newState.setIn(
          ['alerts', 'errors'],
          fromJS(processErrors({ action, strings: state.get('strings') }))
        );
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_FETCH_SALESROUTE_FAIL);
        return newState;
      }

      /**
       * FETCH_RELATIONS
       */
      case types.FETCH_RELATIONS_REQUEST: {
        newState = state.setIn(['loading', 'isLoadingRelations'], true);
        return newState;
      }

      case types.FETCH_RELATIONS_SUCCESS: {
        // getRelation
        newState = state.setIn(['relations'], Immutable.fromJS(action.data));
        newState = newState.setIn(['loading', 'isLoadingRelations'], false);

        return newState;
      }

      case types.FETCH_RELATIONS_FAIL: {
        newState = state.setIn(['loading', 'isLoadingRelations'], false);
        newState = newState.setIn(['loading', 'isLoadingQuestionnaireBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingLoansBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingQuotationBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingSubscriptionBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingDocumentsBlock'], false);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      /**
       * FETCH_QUESTIONNAIRES
       */

      case types.FETCH_QUESTIONNAIRES_REQUEST: {
        newState = state.setIn(['loading', 'isLoadingQuestionnaires'], true);
        return newState;
      }

      case types.FETCH_QUESTIONNAIRES_SUCCESS: {
        // getQuestionnaires
        newQuestionnaires = Immutable.fromJS(getQuestionnairesTransform({ questionnaires: action.data }));
        newState = state.setIn(['questionnaires'], newQuestionnaires);

        const idSalesRoute = get(action, 'idSalesRoute');
        const salesRouteJs = findSalesRouteById({ list: state.getIn(['list']), id: idSalesRoute }).toJS();

        // build questionnaire block
        newState = addBlockToState({ block: 'blockQuestionnaire', state: newState, salesRoute: salesRouteJs });

        // build quotation block
        newState = addBlockToState({
          block: 'blockQuotation',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            !get(salesRouteJs, 'parties', []).length &&
            salesRouteJs.status !== statusSalesRouteEnum.PRESIMULATED &&
            salesRouteJs.status !== statusSalesRouteEnum.LOANSDESCRIBED,
        });

        // build subscription block
        newState = addBlockToState({
          block: 'blockSubscription',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            (salesRouteJs.status !== statusSalesRouteEnum.SIMULATED &&
              salesRouteJs.status !== statusSalesRouteEnum.SUBSCRIBED) ||
            !salesRouteJs.contracts,
        });

        newState = newState.setIn(['loading', 'isLoadingQuestionnaires'], false);
        newState = newState.setIn(['loading', 'isLoadingQuestionnaireBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingLoansBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingQuotationBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingSubscriptionBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingDocumentsBlock'], false);

        return newState;
      }

      case types.FETCH_QUESTIONNAIRES_FAIL: {
        newState = state.setIn(['loading', 'isLoadingQuestionnaires'], false);
        newState = newState.setIn(['loading', 'isLoadingLoansBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingQuotationBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingQuestionnaireBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingSubscriptionBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingDocumentsBlock'], false);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      /**
       * FETCH_PRODUCT_DESCRIPTIONS
       */
      case types.FETCH_PRODUCT_DESCRIPTIONS_REQUEST: {
        newState = state.setIn(['loading', 'isLoadingProductDescriptions'], true);
        return newState;
      }

      case types.FETCH_PRODUCT_DESCRIPTIONS_SUCCESS: {
        // get products
        newProducts = Immutable.fromJS(getProductDescriptionsTransform(get(action, 'data')));
        newState = state.setIn(['products'], newProducts);

        const idSalesRoute = get(action, 'idSalesRoute');
        const salesRouteJs = findSalesRouteById({ list: state.getIn(['list']), id: idSalesRoute }).toJS();

        // build offers block
        newState = addBlockToState({
          block: 'blockOffers',
          state: newState,
          salesRoute: salesRouteJs,
          reset:
            salesRouteJs.status === statusSalesRouteEnum.COMPUTED ||
            salesRouteJs.status === statusSalesRouteEnum.CREATED ||
            salesRouteJs.status === statusSalesRouteEnum.PRESIMULATED ||
            salesRouteJs.status === statusSalesRouteEnum.LOANSDESCRIBED ||
            salesRouteJs.status === statusSalesRouteEnum.COVEREDDESCRIBED,
        });

        newState = newState.setIn(['loading', 'isLoadingProductDescriptions'], false);
        newState = newState.setIn(['loading', 'isLoadingOffersBlock'], false);
        return newState;
      }

      case types.FETCH_PRODUCT_DESCRIPTIONS_FAIL: {
        newState = state.setIn(['loading', 'isLoadingProductDescriptions'], false);
        newState = newState.setIn(['loading', 'isLoadingLoansBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingQuotationBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingSubscriptionBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingDocumentsBlock'], false);
        newState = newState.setIn(['loading', 'isLoadingOffersBlock'], false);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      /**
       * CREATE_SALESROUTE
       */
      case types.CREATE_SALESROUTE_REQUEST: {
        newState = state.setIn(['loading', 'isCreatingSalesRoute'], true);
        newState = newState.setIn(['current'], null);
        return newState;
      }

      case types.CREATE_SALESROUTE_SUCCESS: {
        // set/update salesroute in store
        try {
          newSalesRoute = fromJS(transformResult(action.data));
          newSalesRoute = state.getIn(['map', action.id])
            ? state.getIn(['map', action.id]).mergeDeep(newSalesRoute)
            : newSalesRoute;
          newState = state.setIn(['map', String(action.id)], newSalesRoute);
          newState = newState.setIn(['list', state.getIn(['list'])?.size], newSalesRoute);
          newState = newState.setIn(['loading', 'isCreatingSalesRoute'], false);
          newState = newState.setIn(['current'], action.id);
          return newState;
        } catch (error) {
          return state;
        }
      }

      case types.CREATE_SALESROUTE_FAIL: {
        newState = state.setIn(['loading', 'isCreatingSalesRoute'], false);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        newState = newState.setIn(['current'], null);
        return newState;
      }

      /**
       * CHANGE SALESROUTE STATUS
       */
      case types.PUTONHOLD_SALESROUTE_REQUEST: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_ONHOLD_REQUEST);
        return newState;
      }

      case types.CANCEL_SALESROUTE_REQUEST: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_CLOSED_REQUEST);
        return newState;
      }

      case types.PUTONHOLD_SALESROUTE_SUCCESS: {
        // set/update salesroute in store
        let { state: newState } = getSalesRouteAndState({ state, action });

        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_ONHOLD_SUCCESS);
        newState = newState.setIn(
          ['alerts', 'successes'],
          fromJS([getSuccessMessage('Le statut a été mis à jour avec succès')])
        );
        return newState;
      }

      case types.CANCEL_SALESROUTE_SUCCESS: {
        // set/update salesroute in store
        let { state: newState } = getSalesRouteAndState({ state, action });

        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_CLOSED_SUCCESS);
        newState = newState.setIn(
          ['alerts', 'successes'],
          fromJS([getSuccessMessage('Le statut a été mis à jour avec succès')])
        );
        return newState;
      }

      case types.PUTONHOLD_SALESROUTE_FAIL: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_ONHOLD_FAIL);
        newState = newState.setIn(
          ['alerts', 'errors'],
          fromJS(processErrors({ action, strings: state.get('strings') }))
        );
        return newState;
      }

      case types.CANCEL_SALESROUTE_FAIL: {
        newState = state.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_CLOSED_FAIL);
        newState = newState.setIn(
          ['alerts', 'errors'],
          fromJS(processErrors({ action, strings: state.get('strings') }))
        );
        return newState;
      }

      // PRINT SALESROUTE
      case types.PRINT_SALESROUTE_REQUEST: {
        newState = state.setIn(['loading', 'isPrintingStatus'], true);
        // newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_PRINT_SALESROUTE_REQUEST);
        return newState;
      }

      case types.PRINT_SALESROUTE_SUCCESS: {
        newState = state.setIn(['loading', 'isPrintingStatus'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_PRINT_SALESROUTE_SUCCESS);
        newState = newState.setIn(
          ['alerts', 'successes'],
          fromJS([getSuccessMessage('Les documents ont été générés avec succès')])
        );
        return newState;
      }

      case types.PRINT_SALESROUTE_FAIL: {
        newState = newState.setIn(['loading', 'isPrintingStatus'], false);
        newState = newState.setIn(['advancement', 'status'], advancementStatusEnum.EVENT_PRINT_SALESROUTE_FAIL);
        newState = newState.setIn(['alerts', 'errors'], getErrorMessage(action));
        return newState;
      }

      // SYNCHRONIZE SALESROUTE
      case types.SYNCHRONIZE_SALESROUTE_REQUEST: {
        newState = state.setIn(['loading', 'isPrintingStatus'], true);
        return newState;
      }

      case types.SYNCHRONIZE_SALESROUTE_SUCCESS: {
        // close window
        if (get(action, 'closeWindow', false)) {
          setTimeout(() => {
            const win = window.open('location', '_self');
            win.close();
          }, 3000);
        }
        newState = state.setIn(['loading', 'isPrintingStatus'], false);
        newState = newState.setIn(
          ['alerts', 'successes'],
          fromJS([
            computeMessage({
              strings: get(state, 'strings'),
              code: 'service.salesroute.message.synchonize.salesroute.success',
              message: 'Les données ont été mise à jour avec succès dans Salesforce',
            }),
          ])
        );
        return newState;
      }

      case types.SYNCHRONIZE_SALESROUTE_FAIL: {
        newState = state.setIn(['loading', 'isPrintingStatus'], false);
        newState = newState.setIn(
          ['alerts', 'warnings'],
          fromJS([
            computeMessage({
              strings: get(state, 'strings'),
              code: 'service.salesroute.message.synchonize.salesroute.warning',
              message: `Les données n'ont pas été mise à jour dans Salesforce`,
            }),
          ])
        );
        return newState;
      }

      case types.NO_UPDATE_SALESROUTE: {
        newState = state.setIn(['alerts', 'errors'], fromJS(processErrors({ action, strings: state.get('strings') })));
        return newState;
      }

      case types.CHANGE_PERSON_TYPE: {
        const salesRouteJs = findSalesRouteById({ list: state.getIn(['list']), id: action.id }).toJS();

        const newSRJS = changePersonTypeContract({ salesRoute: salesRouteJs, newPersonType: action.value });

        try {
          newSalesRoute = fromJS(newSRJS);
          if (state.getIn(['list']).size === 0) {
            newState = state.setIn(['list', 0], newSalesRoute);
          } else {
            const newSalesRouteIndex = findSalesRouteIndexById({ list: state.getIn(['list']), id: get(action, 'id') });
            if (newSalesRouteIndex >= 0) newState = state.setIn(['list', newSalesRouteIndex], newSalesRoute);
          }
        } catch (e) {
          console.error(`getSalesRouteAndState: ${e.stack}`);
        }

        // build subscription block
        newState = addBlockToState({
          block: 'blockSubscription',
          state: newState,
          salesRoute: newSRJS,
          reset:
            (newSRJS.status !== statusSalesRouteEnum.SIMULATED && newSRJS.status !== statusSalesRouteEnum.SUBSCRIBED) ||
            !newSRJS.contracts,
        });

        return newState;
      }

      case dashboardTypes.FETCH_DASHBOARD_REQUEST:
        newState = state.setIn(['current'], null);
        return newState;

      default:
        return state;
    }
  } catch (e) {
    console.log(e.stack);
    return state;
  }
};
export default SalesRoutes;
